import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { FaSearch } from "react-icons/fa";
import NavbarAdmin from "../../Components/Admin/NavbarAdmin";
import Sidebar from "../../Components/Admin/Sidebar";
import Add from "./Add";
import useProduct from "../../hooks/Admin/useProduct";
import useOrders from "../../hooks/Admin/useOrders";
import logo from "../../Assets/logo.png";

// Loading Placeholder Components
const LoadingPlaceholder = () => (
  <LoadingOverlay>
    <SpinnerContainer>
      <LogoSpinnerContainer>
        <LoadingLogo src={logo} alt="Loading Logo" />
        <InnerSpinner />
      </LogoSpinnerContainer>
      <LoadingText>LOADING</LoadingText>
    </SpinnerContainer>
  </LoadingOverlay>
);

const Products = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false);

  const { products, loading: loadingProducts, error: errorProducts } = useProduct();
  const { orders, isLoading: loadingOrders, error: errorOrders, refetch } = useOrders();

  // Fetch orders on mount
  useEffect(() => {
    console.log("Refetching orders...");
    refetch();
  }, [refetch]);

  // Calculate purchased quantities for each product
  const calculatePurchasedQuantities = () => {
    const purchasedMap = {};

    orders.forEach((order) => {
      const productId = order.product_id;
      const quantity = Number(order.quantity) || 0;

      if (productId) {
        if (!purchasedMap[productId]) {
          purchasedMap[productId] = 0;
        }
        purchasedMap[productId] += quantity;
      }
    });

    return purchasedMap;
  };

  const purchasedQuantities = calculatePurchasedQuantities();
  console.log("Purchased Quantities:", purchasedQuantities);

  // Enrich products with purchased and available quantities
  const enrichedProducts = products.map((product) => {
    const purchased = purchasedQuantities[product.id] || 0;
    const totalQuantity = Number(product.quantity) || 0;
    const available = totalQuantity - purchased;

    return {
      ...product,
      purchased,
      available: available < 0 ? 0 : available, // Prevent negative availability
    };
  });

  const filteredProducts = enrichedProducts.filter(
    (product) =>
      product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.seller_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Mock product reviews data
  const productReviews = [
    {
      id: 1,
      reviewer: "Kate Ruaza",
      review: "Lorem ipsum praesent ac massa at ligula reet est iaculis. Vivamus est mist aliquet elit ac nisl.",
      date: "January 12, 2025",
      time: "9:00 am",
    },
    {
      id: 2,
      reviewer: "Kate Ruaza",
      review: "Lorem ipsum praesent ac massa at ligula reet est iaculis. Vivamus est mist aliquet elit ac nisl.",
      date: "January 12, 2025",
      time: "9:00 am",
    },
    {
      id: 3,
      reviewer: "Kate Ruaza",
      review: "Lorem ipsum praesent ac massa at ligula reet est iaculis. Vivamus est mist aliquet elit ac nisl.",
      date: "January 12, 2025",
      time: "9:00 am",
    },
    {
      id: 4,
      reviewer: "Kate Ruaza",
      review: "Lorem ipsum praesent ac massa at ligula reet est iaculis. Vivamus est mist aliquet elit ac nisl.",
      date: "January 12, 2025",
      time: "9:00 am",
    },
    {
      id: 5,
      reviewer: "Kate Ruaza",
      review: "Lorem ipsum praesent ac massa at ligula reet est iaculis. Vivamus est mist aliquet elit ac nisl.",
      date: "January 12, 2025",
      time: "9:00 am",
    },
  ];

  const isLoadingCombined = loadingProducts || loadingOrders;
  const hasError = errorProducts || errorOrders;

  return (
    <PageContainer>
      {isLoadingCombined && <LoadingPlaceholder />}
      <NavbarAdmin setIsSidebarOpen={setIsSidebarOpen} />
      <Container>
        <Sidebar isOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
        <Content $isSidebarOpen={isSidebarOpen}>
          <TitleContainer>
            <Title>PRODUCTS</Title>
            <SearchBox>
              <input
                type="text"
                placeholder="Search product or seller"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="searchIcon" />
            </SearchBox>
          </TitleContainer>

          <Add isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />

          {!isLoadingCombined && (
            <>
              <ProductContainer>
                {hasError ? (
                  <ErrorMessage>{hasError}</ErrorMessage>
                ) : (
                  <TableContainer>
                    <Table>
                      <thead>
                        <tr>
                          <th>No.</th>
                          <th>Seller Name</th>
                          <th>Product Name</th>
                          <th>Category</th>
                          <th>Qty</th>
                          <th>Purchased</th>
                          <th>Available</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(showAll ? filteredProducts : filteredProducts.slice(0, 5)).map((product, index) => {
                          const unit = product.is_kilo
                            ? "per kilo"
                            : product.is_pieces
                            ? "per piece/s"
                            : "";
                          const formattedPrice = parseFloat(product.price).toFixed(2);

                          return (
                            <tr key={product.id}>
                              <td>{index + 1}</td>
                              <td>{product.seller_name || ""}</td>
                              <td>
                                <ProductItem>{product.product_name || ""}</ProductItem>
                              </td>
                              <td>{product.category || ""}</td>
                              <td>{Math.floor(Number(product.quantity)) || 0}</td>
                              <td>{product.purchased || 0}</td>
                              <td>{product.available || 0}</td>
                              <td>{formattedPrice ? `₱${formattedPrice} ${unit}` : ""}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </TableContainer>
                )}

                {filteredProducts.length > 5 && (
                  <ShowAllButton onClick={() => setShowAll(!showAll)}>
                    {showAll ? "Show Less" : "Show All Products"}
                  </ShowAllButton>
                )}
              </ProductContainer>

            </>
          )}
        </Content>
      </Container>
    </PageContainer>
  );
};

export default Products;

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background: #f9f9f9;
  position: relative;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  background: #eaf4e1;
`;

const Content = styled.div`
  padding: 20px;
  width: ${({ $isSidebarOpen }) =>
    $isSidebarOpen ? "calc(100% - 240px)" : "calc(100% - 100px)"};
  margin-left: ${({ $isSidebarOpen }) => ($isSidebarOpen ? "200px" : "60px")};
  transition: all 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ccc;
  padding-bottom: 10px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: #21551c;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: white;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid #21551c;
  width: 300px;

  input {
    border: none;
    flex: 1;
    padding: 8px;
    outline: none;
    width: 100%;
  }

  .searchIcon {
    color: #21551c;
    margin-right: 5px;
  }
`;

const ProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 97%;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: hidden; /* Remove horizontal scrolling */
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed; /* Enforce fixed column widths */
  text-align: left;

  th,
  td {
    padding: 8px; /* Reduced padding for compactness */
    border-bottom: 1px solid #ccc;
    overflow: hidden; /* Hide overflow content */
    text-overflow: ellipsis; /* Add ellipsis for truncated text */
    white-space: nowrap; /* Prevent text wrapping */
  }

  th {
    background-color: #f4f4f4;
    color: #21551c;
    font-size: 12px; /* Smaller font for headers */
    width: auto; /* Allow width to adjust */
  }

  td {
    font-size: 15px; /* Smaller font for data */
  }

  /* Define specific column widths */
  th:nth-child(1), td:nth-child(1) { width: 40px; } /* No. */
  th:nth-child(2), td:nth-child(2) { width: 80px; } /* Seller Name */
  th:nth-child(3), td:nth-child(3) { width: 100px; } /* Product Name */
  th:nth-child(4), td:nth-child(4) { width: 80px; } /* Category */
  th:nth-child(5), td:nth-child(5) { width: 50px; } /* Qty */
  th:nth-child(6), td:nth-child(6) { width: 60px; } /* Purchased */
  th:nth-child(7), td:nth-child(7) { width: 60px; } /* Available */
  th:nth-child(8), td:nth-child(8) { width: 80px; } /* Price */
`;

const ProductItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px; /* Reduced gap for compactness */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ShowAllButton = styled.button`
  margin-top: 10px;
  background: none;
  border: none;
  color: #21551c;
  font-weight: bold;
  cursor: pointer;
  font-size: 14px; /* Slightly smaller font */

  &:hover {
    text-overflow: underline;
  }
`;

const ErrorMessage = styled.p`
  color: red;
  font-size: 16px;
  font-weight: bold;
`;

// Reviews Section Styled Components
const ReviewsContainer = styled.div`
  width: 97%;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 30px;
`;

const ReviewsTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  color: #21551c;
  margin-bottom: 20px;
`;

const ReviewCard = styled.div`
  background: #f4f4f4;
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 10px;
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const ReviewerName = styled.span`
  font-weight: bold;
  color: #333;
`;

const ReviewDate = styled.span`
  font-size: 14px;
  color: #666;
`;

const ReviewText = styled.p`
  color: #666;
  font-size: 12px; /* Smaller font for reviews */
  margin: 0;
`;

// Loading Placeholder Styled Components
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoSpinnerContainer = styled.div`
  position: relative;
  width: 50px;
  height: 50px;
`;

const LoadingLogo = styled.img`
  width: 50px;
  height: 50px;
  z-index: 1;
  border-radius: 50%;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const InnerSpinner = styled.div`
  position: absolute;
  top: -5px;
  left: -5px;
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.5);
  border-top: 3px solid #004d00;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  pointer-events: none;
`;

const LoadingText = styled.span`
  color: #004d00;
  font-size: 1.2rem;
  font-weight: bold;
  margin-top: 10px;

  &::after {
    content: ".";
    animation: dots 1.5s steps(3, end) infinite;
    margin-left: 5px;
  }

  @keyframes dots {
    0% { content: "."; }
    33% { content: ".."; }
    66% { content: "..."; }
    100% { content: "."; }
  }
`;